import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, ClipboardList, CheckCircle2, Circle, ShieldCheck, Upload, FileText, FileSignature, ChevronLeft, Download, Mail } from "lucide-react";

/**
 * Renders when a portal link is expired/revoked/invalid. Lets the client
 * ask staff for a new secure link WITHOUT leaking whether the email is
 * associated with any client (backend always returns the same body).
 */
function ExpiredPortalCard() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setSending(true);
    try {
      await publicFetch(`/portal/reissue-request`, {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (_) {
      // Even on transient error, keep the friendly generic message
      setSent(true);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center p-6" data-testid="portal-expired-card">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#FDDCDC] flex items-center justify-center mx-auto mb-4">
          <ShieldCheck size={22} className="text-[#B02222]" />
        </div>
        <h2 className="font-display font-bold text-xl tracking-tight mb-2">This portal link has expired</h2>
        <p className="text-sm text-[#57534E]">Please request a new secure link. We'll notify your provider so they can send one.</p>
        {sent ? (
          <div className="mt-6 rounded-2xl bg-[#E8F0E4] text-[#1F6B36] px-4 py-3 text-sm" data-testid="portal-reissue-confirmation">
            If we recognize that email, your provider has been notified. They will reach out with a new secure link.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3 text-left">
            <label className="text-xs font-semibold text-[#57534E]">Your email address</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="portal-reissue-email"
              className="w-full px-3 py-2 rounded-xl border border-[#E8E2D9] bg-[#FAF8F5] text-sm"
            />
            <Button
              type="submit"
              disabled={sending || !email.includes("@")}
              className="w-full rounded-full bg-[#E8712C] hover:bg-[#D45E20] text-white"
              data-testid="portal-reissue-submit"
            >
              <Mail size={14} className="mr-1.5" />
              {sending ? "Sending…" : "Request a new link"}
            </Button>
            <p className="text-[11px] text-[#A8A29E] mt-2">We never confirm whether an email is registered. If it is, your provider will follow up privately.</p>
          </form>
        )}
      </div>
    </div>
  );
}
import Logo from "@/components/Logo";
import { openExternal } from "@/lib/external";

const REQUIRED = [
  { key: "client_info", label: "Client information" },
  { key: "contact_info", label: "Contact information" },
  { key: "insurance_info", label: "Insurance information" },
  { key: "referral_info", label: "Referral information" },
  { key: "required_documents", label: "Required documents" },
  { key: "provider_info", label: "Provider information" },
  { key: "final_review", label: "Final review" },
];

async function publicFetch(path, options = {}) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { const j = await res.json(); detail = j.detail || j.message || detail; } catch (_) {}
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return res.json();
}

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [assignedForms, setAssignedForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const [formAnswers, setFormAnswers] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [contracts, setContracts] = useState([]);

  const loadDocs = async () => {
    try {
      const r = await publicFetch(`/portal/${token}/documents`);
      setDocs(r.documents || []);
    } catch (_) {}
  };

  const loadAssignedForms = async () => {
    setLoadingForms(true);
    try {
      const r = await publicFetch(`/portal/${token}/forms`);
      setAssignedForms(r.forms || []);
    } catch (_) {}
    finally { setLoadingForms(false); }
  };

  const loadInvoices = async () => {
    try {
      const r = await publicFetch(`/portal/${token}/invoices`);
      setInvoices(r.invoices || []);
    } catch (_) {}
  };

  const loadContracts = async () => {
    try {
      const r = await publicFetch(`/portal/${token}/contracts`);
      setContracts(r.contracts || []);
    } catch (_) {}
  };

  const downloadContract = (id, name) => {
    // Backend redirects to a short-lived signed URL. openExternal() routes
    // to the Capacitor Browser plugin on native so cookies + redirects work;
    // on the web it uses window.open() as before.
    const url = `${BACKEND_URL}/api/portal/${token}/contracts/${id}/pdf`;
    openExternal(url);
  };

  const payInvoice = async (id) => {
    setPayingId(id);
    try {
      const r = await publicFetch(`/portal/${token}/invoices/${id}/checkout`, { method: "POST", body: "{}" });
      if (r.url) window.location.href = r.url;
    } catch (e) { toast.error(e.message); }
    finally { setPayingId(null); }
  };

  const openAssignedForm = async (assignmentId) => {
    try {
      const r = await publicFetch(`/portal/${token}/forms/${assignmentId}`);
      const template = r.assignment?.form_templates || {};
      setActiveForm({ ...r, template, assignmentId });
      const initial = { ...(r.prefill || {}) };
      if (r.saved?.responses) Object.assign(initial, r.saved.responses);
      setFormAnswers(initial);
    } catch (e) { toast.error(e.message); }
  };

  const submitAssignedForm = async () => {
    if (!activeForm) return;
    setSubmittingForm(true);
    try {
      await publicFetch(`/portal/${token}/forms/${activeForm.assignmentId}/submit`, {
        method: "POST",
        body: JSON.stringify({ responses: formAnswers }),
      });
      // Prefer the template's configured success_message so intake forms can
      // show a warm brand-specific thank-you instead of a generic toast.
      const sm = activeForm.template?.success_message;
      toast.success(sm && sm.trim() ? sm : "Form submitted — thank you!");
      setActiveForm(null);
      setFormAnswers({});
      loadAssignedForms();
    } catch (e) {
      if (typeof e.message === "string" && e.message.includes("missing_required_fields")) {
        toast.error("Please complete the required fields marked with *");
      } else {
        toast.error(e.message);
      }
    } finally { setSubmittingForm(false); }
  };

  useEffect(() => {
    publicFetch(`/portal/${token}`)
      .then((d) => {
        setData(d);
        setChecklist(d.intake?.checklist || {});
        setReason(d.intake?.reason || "");
        setLoading(false);
        loadDocs();
        loadAssignedForms();
        loadInvoices();
        loadContracts();
      })
      .catch((e) => { setError(e.message); setLoading(false); });
    // eslint-disable-next-line
  }, [token]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File must be under 20 MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", "intake_form");
    fd.append("name", file.name);
    try {
      const res = await fetch(`${BACKEND_URL}/api/portal/${token}/documents`, { method: "POST", body: fd });
      if (!res.ok) {
        let d = res.statusText; try { const j = await res.json(); d = j.detail || d; } catch (_) {}
        throw new Error(typeof d === "string" ? d : JSON.stringify(d));
      }
      toast.success("Uploaded");
      e.target.value = "";
      loadDocs();
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const toggle = (k) => setChecklist((c) => ({ ...c, [k]: !c[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await publicFetch(`/portal/${token}/intake`, {
        method: "POST",
        body: JSON.stringify({ checklist, reason: reason || undefined }),
      });
      toast.success("Saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] text-[#57534E]">Loading…</div>;
  if (error) return <ExpiredPortalCard />;

  const progress = Math.round((REQUIRED.filter((r) => checklist[r.key]).length / REQUIRED.length) * 100);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="portal-page">
      <header className="bg-[#2C3A29] text-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Logo variant="full" size={36} onDark />
          <div className="text-xs text-[#A8A29E]">Secure client portal</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <div className="text-xs text-[#57534E] uppercase tracking-widest mb-2">{data.org_name}</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-1">
            Hi {data.client.preferred_name || data.client.first_name}
          </h1>
          <p className="text-sm text-[#57534E]">Welcome to your private space. Everything you complete here is shared securely with your provider.</p>
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-appts">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">My bookings</h2>
          </div>
          <PortalBookings token={token} />
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-legacy-appts">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">Upcoming appointments</h2>
          </div>
          {data.upcoming_appointments.length === 0 ? (
            <p className="text-sm text-[#A8A29E]">No upcoming appointments right now.</p>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {data.upcoming_appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-center gap-4">
                  <div className="w-24 flex-shrink-0">
                    <div className="text-sm font-semibold text-[#E8712C]">{new Date(a.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                    <div className="text-xs text-[#A8A29E]">{new Date(a.starts_at).toLocaleDateString([], { month: "short", day: "numeric" })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.title}</div>
                    <div className="text-xs text-[#57534E]">
                      {a.appointment_type}
                      {a.location && ` · ${a.location}`}
                    </div>
                    {a.meeting_link && (
                      <a href={a.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-[#E8712C] hover:underline">Join link →</a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-invoices">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">Invoices</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="text-sm text-[#A8A29E]">No invoices to review right now.</p>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {invoices.map((inv) => {
                const paid = inv.status === "paid";
                return (
                  <li key={inv.id} className="py-3 flex items-center gap-4" data-testid={`portal-inv-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{inv.invoice_number}</div>
                      <div className="text-xs text-[#57534E]">
                        {inv.due_date ? `Due ${new Date(inv.due_date).toLocaleDateString()}` : "No due date"} · {inv.currency.toUpperCase()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">${((inv.total_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div className={`text-[10px] uppercase tracking-widest ${paid ? "text-[#1F6B36]" : "text-[#B85C1F]"}`}>{inv.status}</div>
                    </div>
                    {!paid && (
                      <Button size="sm" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" onClick={() => payInvoice(inv.id)} disabled={payingId === inv.id} data-testid={`portal-pay-${inv.id}`}>
                        {payingId === inv.id ? "Opening…" : "Pay now"}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-contracts">
          <div className="flex items-center gap-2 mb-4">
            <FileSignature size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">My contracts</h2>
          </div>
          {contracts.length === 0 ? (
            <p className="text-sm text-[#A8A29E]">No signed contracts on file yet.</p>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {contracts.map((c) => {
                const status = c.status;
                const isTerminated = status === "terminated";
                const isExpired = status === "expired";
                const tone = isTerminated
                  ? "text-[#B02222]"
                  : isExpired
                    ? "text-[#57534E]"
                    : "text-[#1F6B36]";
                return (
                  <li
                    key={c.id}
                    className="py-3 flex flex-col gap-2"
                    data-testid={`portal-contract-${c.id}`}
                  >
                    <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {c.contract_name || c.contract_number}
                      </div>
                      <div className="text-xs text-[#57534E] mt-0.5 space-x-2">
                        {c.signed_at && <span>Signed {new Date(c.signed_at).toLocaleDateString()}</span>}
                        {c.effective_date && <span>· Effective {new Date(c.effective_date).toLocaleDateString()}</span>}
                        {isTerminated && <span className="text-[#B02222]">· Terminated</span>}
                        {isExpired && <span>· Expired {c.end_date ? new Date(c.end_date).toLocaleDateString() : ""}</span>}
                      </div>
                    </div>
                    <div className={`text-[10px] uppercase tracking-widest ${tone}`}>{status}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => downloadContract(c.id, c.contract_name)}
                      data-testid={`portal-contract-download-${c.id}`}
                    >
                      <Download size={14} className="mr-1.5" /> View PDF
                    </Button>
                    </div>
                    {c.finalized_source === "legacy_backfill" && (
                      <div
                        data-testid={`portal-contract-legacy-note-${c.id}`}
                        className="text-xs bg-[#FBF3E4] border border-[#E8D9BA] text-[#6B5A1F] rounded-xl px-3 py-2"
                      >
                        <span className="font-semibold text-[#5A4A15]">Historical document notice:</span>{" "}
                        This PDF was recreated from available historical contract data because the
                        original finalized PDF was not stored.
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-intake">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">Your intake</h2>
          </div>
          <p className="text-sm text-[#57534E] mb-4">Check off what you've provided so your provider knows what's ready.</p>

          <div className="h-2 rounded-full bg-[#F5EDE4] overflow-hidden mb-1">
            <div className="h-full rounded-full bg-[#E8712C] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-xs text-[#57534E] mb-4">{progress}% complete</div>

          <div className="space-y-2 mb-6">
            {REQUIRED.map((r) => (
              <button key={r.key} onClick={() => toggle(r.key)} data-testid={`portal-chk-${r.key}`}
                className="w-full flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#FAF8F5] text-left">
                {checklist[r.key] ? <CheckCircle2 size={18} className="text-[#E8712C]" /> : <Circle size={18} className="text-[#A8A29E]" />}
                <span className={`text-sm ${checklist[r.key] ? "line-through text-[#A8A29E]" : "text-[#1C1917]"}`}>{r.label}</span>
              </button>
            ))}
          </div>

          <div>
            <div className="text-sm font-medium mb-1.5">Anything you'd like your provider to know?</div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Optional — share the reason for your visit or any context that helps." data-testid="portal-reason" />
          </div>

          <div className="mt-5 text-right">
            <Button data-testid="portal-save" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              {saving ? "Saving…" : "Save my progress"}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-forms">
          <div className="flex items-center gap-2 mb-1">
            <FileSignature size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">Forms to complete</h2>
          </div>
          <p className="text-sm text-[#57534E] mb-4">Forms your provider has shared with you.</p>
          {loadingForms ? (
            <p className="text-sm text-[#A8A29E]">Loading your forms…</p>
          ) : assignedForms.length === 0 ? (
            <p className="text-sm text-[#A8A29E]">You have no forms to complete right now.</p>
          ) : (
            <ul className="divide-y divide-[#E8E2D9] border-t border-[#E8E2D9]">
              {assignedForms.map((f) => {
                const submitted = f.status === "submitted";
                return (
                  <li key={f.id} className="py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{f.form_templates?.name || "Form"}</div>
                      {f.form_templates?.description && <div className="text-xs text-[#57534E] truncate">{f.form_templates.description}</div>}
                      <div className="text-[10px] uppercase tracking-widest mt-1">
                        <span className={`px-2 py-0.5 rounded-full ${submitted ? "bg-[#E8F0E4] text-[#2C3A29]" : f.status === "in_progress" ? "bg-[#FDE9D8] text-[#C55A1F]" : "bg-[#F5F5F4] text-[#57534E]"}`}>
                          {submitted ? "Submitted" : f.status === "in_progress" ? "In progress" : "Not started"}
                        </span>
                      </div>
                    </div>
                    <Button data-testid={`portal-open-form-${f.id}`} onClick={() => openAssignedForm(f.id)}
                      variant={submitted ? "outline" : "default"}
                      className={`rounded-full ${submitted ? "" : "bg-[#E8712C] hover:bg-[#C55A1F] text-white"}`}>
                      {submitted ? "View" : (f.status === "in_progress" ? "Continue" : "Start")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="portal-docs">
          <div className="flex items-center gap-2 mb-1">
            <Upload size={18} className="text-[#E8712C]" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-lg tracking-tight">Upload documents</h2>
          </div>
          <p className="text-sm text-[#57534E] mb-4">Share ID, insurance cards, referrals or intake forms. Files stay private to your provider.</p>
          <label className="inline-block cursor-pointer">
            <input type="file" hidden onChange={uploadFile} disabled={uploading} data-testid="portal-file-input" />
            <span className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${uploading ? "bg-[#F5F5F4] text-[#A8A29E]" : "bg-[#E8712C] text-white hover:bg-[#C55A1F]"}`}>
              <Upload size={14} /> {uploading ? "Uploading…" : "Choose a file"}
            </span>
          </label>
          {docs.length > 0 && (
            <ul className="mt-5 divide-y divide-[#E8E2D9] border-t border-[#E8E2D9] pt-3">
              {docs.map((d) => (
                <li key={d.id} className="py-2 flex items-center gap-3">
                  <FileText size={16} className="text-[#E8712C] flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.name}</div>
                    <div className="text-xs text-[#A8A29E]">{new Date(d.created_at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-center text-xs text-[#A8A29E] py-4">Powered by ClientDesk · This link is private to you.</p>
      </div>

      {activeForm && (
        <PortalFormFiller
          data={activeForm}
          answers={formAnswers}
          onChange={setFormAnswers}
          onClose={() => { setActiveForm(null); setFormAnswers({}); }}
          onSubmit={submitAssignedForm}
          submitting={submittingForm}
        />
      )}
    </div>
  );
}

function PortalFormFiller({ data, answers, onChange, onClose, onSubmit, submitting }) {
  const t = data.template || {};
  const submitted = data.assignment?.status === "submitted";
  return (
    <div className="fixed inset-0 z-50 bg-[#FAF8F5] overflow-y-auto" data-testid="portal-form-filler">
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <button onClick={onClose} className="text-sm text-[#57534E] flex items-center gap-1.5 hover:text-[#1C1917] mb-4" data-testid="portal-form-back">
          <ChevronLeft size={14} /> Back to my portal
        </button>
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <h1 className="font-display font-bold text-2xl tracking-tight mb-1">{t.name}</h1>
          {t.description && <p className="text-sm text-[#57534E] mb-5">{t.description}</p>}
          {submitted && (
            <div className="bg-[#E8F0E4] text-[#2C3A29] text-sm rounded-xl px-3 py-2 mb-4">
              You've already submitted this form. It's shown below in read-only mode.
            </div>
          )}
          <div className="space-y-4">
            {(t.fields || []).map((f, i) => (
              <PortalField key={f.id || i} field={f}
                value={answers[f.id || f.label] ?? ""}
                onChange={(v) => onChange({ ...answers, [f.id || f.label]: v })}
                disabled={submitted}
              />
            ))}
          </div>
          {!submitted && (
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button data-testid="portal-form-submit" onClick={onSubmit} disabled={submitting} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                {submitting ? "Submitting…" : (t.submit_button_label || "Submit")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalField({ field, value, onChange, disabled }) {
  const key = field.id || field.label;
  const label = <Label className="text-sm">{field.label}{field.required && <span className="text-[#E8712C]"> *</span>}</Label>;
  const common = { id: key, value: value || "", onChange: (e) => onChange(e.target.value), placeholder: field.placeholder || "", disabled, "data-testid": `portal-field-${key}` };
  if (field.type === "textarea") return <div>{label}<Textarea rows={3} {...common} className="mt-1.5" /></div>;
  if (field.type === "select") return (
    <div>{label}
      <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="mt-1.5" data-testid={`portal-field-${key}`}><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>{(field.options || []).filter((o) => o && String(o).trim()).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (field.type === "checkbox") return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={value === true || value === "true"} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#E8712C]" data-testid={`portal-field-${key}`} />
      {field.label}
    </label>
  );
  if (field.type === "radio") return (
    <div>{label}
      <div className="mt-1.5 space-y-1.5">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name={key} value={o} checked={value === o} disabled={disabled} onChange={() => onChange(o)} className="accent-[#E8712C]" />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
  const inputType = { number: "number", date: "date", email: "email", phone: "tel" }[field.type] || "text";
  return <div>{label}<Input type={inputType} {...common} className="mt-1.5" /></div>;
}


function PortalBookings({ token }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("upcoming");
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/portal/${token}/bookings`)
      .then((r) => r.json()).then(setData).catch(() => setData({ upcoming: [], past: [], cancelled: [] }));
  }, [token]);
  if (!data) return <p className="text-xs text-[#A8A29E]">Loading…</p>;
  const list = data[tab] || [];
  const label = { google_meet: "Google Meet", teams: "Microsoft Teams", zoom_manual: "Zoom", phone: "Phone", in_person: "In person", custom_link: "Meeting link" };
  return (
    <div>
      <div className="flex gap-2 border-b border-[#E8E2D9] mb-3">
        {[["upcoming","Upcoming"],["past","Past"],["cancelled","Cancelled"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`portal-bk-tab-${k}`}
            className={`px-3 py-1.5 text-sm border-b-2 ${tab===k?"border-[#E8712C] text-[#1C1917] font-medium":"border-transparent text-[#57534E]"}`}>
            {l} <span className="text-xs text-[#A8A29E]">({(data[k]||[]).length})</span>
          </button>
        ))}
      </div>
      {list.length === 0 && <p className="text-xs text-[#A8A29E]">No {tab} bookings.</p>}
      <ul className="divide-y divide-[#E8E2D9]">
        {list.map((b) => {
          const s = b.appointment_type_snapshot || {};
          return (
            <li key={b.id} className="py-3" data-testid={`portal-bk-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{s.name || "Appointment"}</div>
                  <div className="text-xs text-[#57534E]">
                    {new Date(b.starts_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {" · "}{b.duration_minutes} min · {label[b.location_type] || b.location_type}
                  </div>
                  {b.status === "cancelled" && b.cancellation_reason && <div className="text-xs text-[#B02222] mt-0.5">Reason: {b.cancellation_reason}</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {tab === "upcoming" && b.meeting_url && (
                    <a href={b.meeting_url} target="_blank" rel="noreferrer" data-testid={`portal-bk-join-${b.id}`}
                       className="text-xs px-3 py-1 rounded-full bg-[#E8712C] text-white">Join</a>
                  )}
                  {tab === "upcoming" && b.manage_token && (
                    <a href={`/booking/manage/${b.manage_token}`} target="_blank" rel="noreferrer"
                       data-testid={`portal-bk-manage-${b.id}`}
                       className="text-xs px-3 py-1 rounded-full border border-[#E8E2D9]">Reschedule / Cancel</a>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
